/**
 * 自带 AI 的文案框 —— 「优化前后**同一个框**」(2026-08-01)。
 *
 * ═══ 三条口径,都不是新发明的 ═══════════════════════════════════════════════
 * ① **同一个框**:沿用创作页那版已定的做法(`FinalPromptFold`),不另开对照面板。
 *    改完就在原地,旁边一行小字说「AI 改过 · 看原文 · 撤回」。
 * ② **改了给你看 + 可撤回**:AI 落笔属于「创作选择」,不能悄悄换掉 ——
 *    见设计稿「每个字段有主人」:用户随时能把控制权收回来。
 * ③ **失败要说话**:ai 侧任何一环出问题都退回原文 + `degraded=true` + reason。
 *    它不该弹红色报错(用户只是点了个润色按钮),但也**不许假装改过了**。
 */
import { maskVendor } from "@/shared/lib/vendorMask";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Loader2, Sparkles } from "lucide-react";

import { toast } from "@/shared/hooks/use-toast";
import { Textarea } from "@/shared/ui/textarea";
import { Input } from "@/shared/ui/input";
import { cn } from "@/shared/lib/utils";
import { posterService } from "../../api/posterService";

interface Props {
  label: string;
  value: string;
  onChange: (v: string) => void;
  /** 数字白名单的锚(用户原文 + 图上 OCR 原文 + 产品库原文);空则 AI 按钮不出现。 */
  rawBlob?: string;
  /** 「首屏主图」—— 让模型知道这句话摆在哪一段。 */
  blockLabel?: string;
  multiline?: boolean;
  rows?: number;
  maxLength?: number;
  /**
   * 紧凑态:AI 按钮只出图标(hover/无障碍名仍是「AI 优化:{label}」)。
   * 给同一张卡里的**次要短字段**用(2026-08-10 用户:「同一个框有两个 AI 优化按钮,
   * 是不是重复了」)—— 两枚一模一样的文字钮上下摞着,分不清各管哪个字段;
   * 主字段保留带字钮,次字段收成图标,视觉上一主一辅。
   */
  compact?: boolean;
}

export function RefinableField({
  label,
  value,
  onChange,
  rawBlob,
  blockLabel,
  multiline = false,
  rows = 3,
  maxLength = 600,
  compact = false,
}: Props) {
  /** 上一版原文;null = 这个框还没被 AI 动过。 */
  const [original, setOriginal] = useState<string | null>(null);
  const [showOriginal, setShowOriginal] = useState(false);

  const refine = useMutation({
    mutationFn: () =>
      posterService.refineLongImageCopy({
        text: value,
        rawBlob: rawBlob ?? "",
        blockLabel: blockLabel ?? label,
      }),
    onSuccess: (res) => {
      if (res.degraded) {
        // ★不弹红色 destructive:这不是错误,是"这次没改成"。但必须说出来。
        toast({ title: "这次没改成", description: maskVendor(res.reason) || "原文保持不变" });
        return;
      }
      if (res.text === value) {
        toast({ title: "已经挺好了", description: "AI 觉得原文不用改" });
        return;
      }
      setOriginal(value);
      setShowOriginal(false);
      // ★AI 回写也要吃 maxLength(2026-08-06 线上 422)。
      //   `maxLength` 是 input/textarea 的属性,只拦**手打**;程序赋值一个字都拦不住。
      //   于是「AI 优化」/「一键全部优化」可以把一段超过字段上限的话塞进来,用户在页面上
      //   看不出任何异常,直到点「确认出图」才吃到
      //   `POSTER_INVALID_PARAMS blocks.N.slots.subject —— at most 120 characters`,
      //   而且是**整份产物**被一个格子打回来。
      onChange(res.text.slice(0, maxLength));
    },
    onError: () => toast({ title: "这次没改成", description: "稍后再试,原文保持不变" }),
  });

  const canRefine = !!value.trim() && !refine.isPending;

  return (
    <div>
      <div className="mb-1 flex items-center gap-2">
        <span className="text-2xs font-medium text-muted-foreground">{label}</span>
        <button
          type="button"
          disabled={!canRefine}
          aria-label={`AI 优化：${label}`}
          title={`AI 优化：${label}`}
          onClick={() => refine.mutate()}
          className={cn(
            "ml-auto inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-2xs font-semibold transition-colors",
            canRefine
              ? "border-primary/40 bg-primary/10 text-primary hover:bg-primary/20"
              : "border-border/60 text-muted-foreground opacity-60"
          )}
        >
          {refine.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Sparkles className="h-3 w-3" />
          )}
          {!compact && "AI 优化"}
        </button>
      </div>

      {multiline ? (
        <Textarea
          aria-label={label}
          value={value}
          rows={rows}
          maxLength={maxLength}
          onChange={(e) => onChange(e.target.value)}
          className={cn("text-xs", original !== null && "border-primary/45 bg-primary/5")}
        />
      ) : (
        <Input
          aria-label={label}
          value={value}
          maxLength={maxLength}
          onChange={(e) => onChange(e.target.value)}
          className={cn("h-8 text-xs", original !== null && "border-primary/45 bg-primary/5")}
        />
      )}

      {original !== null && (
        <div data-refined className="mt-1 text-2xs text-muted-foreground">
          <span className="font-semibold text-primary">✦ AI 改过</span>
          {" · "}
          <button
            type="button"
            onClick={() => setShowOriginal((v) => !v)}
            className="underline underline-offset-2"
          >
            {showOriginal ? "收起原文" : "看原文"}
          </button>
          {" · "}
          <button
            type="button"
            onClick={() => {
              onChange(original);
              setOriginal(null);
              setShowOriginal(false);
            }}
            className="underline underline-offset-2"
          >
            撤回
          </button>
          {showOriginal && (
            <p className="mt-1 rounded bg-muted/60 px-2 py-1 text-2xs leading-relaxed">
              {original}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
